import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { auth } from './auth';

const apiClient = axios.create({
    baseURL: 'https://api.ascaa.lamoom.com',
    timeout: 300000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

export const useAuthTokenManager = () => {
    const navigate = useNavigate();
    const addAuthTokenToConfig = async (config) => {
        const requestConfig = config || {};
        try {
            const token = await auth.getValidToken();
            if (token) {
                requestConfig.headers = requestConfig.headers || {};
                requestConfig.headers.Authorization = `Bearer ${token}`;
            }
        } catch (error) {
            console.error('Failed to get valid token, navigating to /login', error);
            navigate('/login');
        }
        return requestConfig;
    };
    const handleAuthError = (error) => {
        if (error.response?.status === 401) {
            console.error('Authentication error, navigating to /login', error);
        }
    };
    return { addAuthTokenToConfig, handleAuthError };
};

export const useApiClient = () => {
    const { addAuthTokenToConfig, handleAuthError } = useAuthTokenManager();
    const get = async (url, config) => {
        try {
            const authConfig = await addAuthTokenToConfig(config);
            const response = await apiClient.get(url, authConfig);
            return response.data;
        } catch (error) {
            console.error('API GET error:', error);
            if (axios.isAxiosError(error)) handleAuthError(error);
            throw error;
        }
    };
    const post = async (url, data, config) => {
        try {
            const authConfig = await addAuthTokenToConfig(config);
            const response = await apiClient.post(url, data, authConfig);
            return response.data;
        } catch (error) {
            console.error('API POST error:', error);
            if (axios.isAxiosError(error)) handleAuthError(error);
            throw error;
        }
    };
    const put = async (url, data, config) => {
        try {
            const authConfig = await addAuthTokenToConfig(config);
            const response = await apiClient.put(url, data, authConfig);
            return response.data;
        } catch (error) {
            console.error('API PUT error:', error);
            if (axios.isAxiosError(error)) handleAuthError(error);
            throw error;
        }
    };
    const patch = async (url, data, config) => {
        try {
            const authConfig = await addAuthTokenToConfig(config);
            const response = await apiClient.patch(url, data, authConfig);
            return response.data;
        } catch (error) {
            console.error('API PATCH error:', error);
            if (axios.isAxiosError(error)) handleAuthError(error);
            throw error;
        }
    };
    const del = async (url, data, config) => {
        try {
            const authConfig = await addAuthTokenToConfig(config);
            const response = await apiClient.delete(url, { ...authConfig, data });
            return response.data;
        } catch (error) {
            console.error('API DELETE error:', error);
            if (axios.isAxiosError(error)) handleAuthError(error);
            throw error;
        }
    };
    return { get, post, put, patch, delete: del };
};

export default apiClient; 